// Revenue (Ordre 17): offers sold to the pharmacies, customer subscriptions, installments, invoice references and payments.
// Labels and pure helpers shared by the screens. Money stays in integer minor units (lib/finance.js), never added across currencies.
import { formatMinor, minorToText, toMinor } from "./finance";
import { hasPermission } from "./permissions";

export const BILLING_VIEW = "billing:view";

export const PERIODICITIES = ["monthly", "quarterly", "yearly"];
export const PERIODICITY_LABELS = { monthly: "Mensuelle", quarterly: "Trimestrielle", yearly: "Annuelle" };
export const PER_PERIOD = { monthly: "/ mois", quarterly: "/ trimestre", yearly: "/ an" };
export const MONTHS = { monthly: 1, quarterly: 3, yearly: 12 };
export const CURRENCIES = ["XOF", "EUR", "USD", "GBP", "CAD", "CHF"];

export const SUBSCRIPTION_STATUS_LABELS = {
  draft: "Brouillon", trial: "En essai", active: "En cours", paused: "En pause", ended: "Terminé", cancelled: "Annulé",
};
export const END_CODE_LABELS = {
  customer_termination: "Résiliation par la pharmacie", offer_change: "Changement d'offre", contract_end: "Fin de contrat", other: "Autre",
};
export const INSTALLMENT_STATE_LABELS = {
  to_invoice: "À facturer", invoiced: "Facturée", partially_paid: "Partiellement payée", paid: "Payée", overdue: "En retard", cancelled: "Annulée",
};
export const AGE_LABELS = { "0_30": "1 à 30 jours", "31_60": "31 à 60 jours", "60_plus": "Plus de 60 jours" };
export const PAYMENT_METHOD_LABELS = {
  transfer: "Virement", wave: "Wave", orange_money: "Orange Money", cash: "Espèces", cheque: "Chèque", other: "Autre",
};
export const REMINDER_CHANNEL_LABELS = { phone: "Téléphone", whatsapp: "WhatsApp", email: "E-mail", visit: "Visite", other: "Autre" };
export const DOCUMENT_KIND_LABELS = { invoice: "Facture", credit_note: "Avoir" };

// What each subscription action is called on a button, and what it needs from the person.
export const ACTION_LABELS = {
  start: "Démarrer", start_billing: "Démarrer la facturation", pause: "Mettre en pause", resume: "Reprendre",
  change_offer: "Changer d'offre", end: "Terminer", cancel: "Annuler le brouillon",
};

export function canSeeBilling(user) {
  return !!user?.features?.billing && hasPermission(user, BILLING_VIEW);
}

// Every write needs billing:view too (D-60): the server refuses otherwise, the screen does not offer it.
export function canWrite(user, key) {
  return canSeeBilling(user) && hasPermission(user, key);
}

export function money(minor, currency) {
  return minor === null || minor === undefined ? "" : formatMinor(minor, currency);
}

export function priceText(minor, currency, periodicity) {
  return minor === null || minor === undefined ? "Sur devis" : `${formatMinor(minor, currency)} ${PER_PERIOD[periodicity] || ""}`.trim();
}

// MRR contribution of one price (D-50): brought back to a month, rounded down to the minor unit — the server's own rule.
export function monthlyMinor(priceMinor, periodicity) {
  return Math.floor(priceMinor / (MONTHS[periodicity] || 1));
}

// A draft's period rows for the editor: every periodicity, on or off, with its price as text.
export function editorPeriods(content) {
  const byKey = Object.fromEntries((content?.periods || []).map((p) => [p.periodicity, p]));
  return PERIODICITIES.map((periodicity) => {
    const row = byKey[periodicity];
    const currency = content?.currency || "XOF";
    return { periodicity, enabled: !!row, price: row && row.price_minor != null ? minorToText(row.price_minor, currency) : "" };
  });
}

// The draft as the API takes it back.
export function contentPayload(draft) {
  const features = draft.featuresText.split("\n").map((l) => l.trim()).filter(Boolean);
  const users = String(draft.users ?? "").trim();
  const trial = String(draft.trialDays ?? "").trim();
  return {
    currency: draft.currency, on_quote: draft.onQuote,
    periods: draft.periods.filter((p) => p.enabled).map((p) => ({ periodicity: p.periodicity, price: draft.onQuote ? null : (p.price.trim() || null) })),
    users_included: users ? Number(users) : null, features, conditions: draft.conditions || "",
    trial_days: trial ? Number(trial) : null, effective_date: draft.effectiveDate || null,
  };
}

// What still blocks publishing a draft, in words (the server checks again).
export function publishProblem(draft) {
  const periods = draft.periods.filter((p) => p.enabled);
  if (!periods.length) return "Indiquez au moins une périodicité.";
  if (!draft.onQuote && periods.some((p) => toMinor(p.price, draft.currency) === null)) return "Chaque périodicité choisie doit avoir un prix valide.";
  return null;
}

// The price a new subscription gets for a periodicity of an offer's published version (null = on quote).
export function listPrice(version, periodicity) {
  const row = (version?.content?.periods || []).find((p) => p.periodicity === periodicity);
  return row ? row.price_minor : null;
}

// Whether a typed price differs from the list price, so a reason is required (D-21).
export function needsPriceReason(version, periodicity, priceText_) {
  const list = listPrice(version, periodicity);
  const typed = String(priceText_ ?? "").trim();
  if (!typed) return false; // the list price applies (on quote, the form asks for the price itself)
  const minor = toMinor(typed, version?.content?.currency || "XOF");
  return minor !== null && minor !== list;
}

export function addDays(iso, days) {
  const [y, m, d] = iso.split("-").map(Number);
  const date = new Date(Date.UTC(y, m - 1, d + days));
  return date.toISOString().slice(0, 10);
}

// The price a subscription would take on its offer's newer version (`newer_version` from the server): same periodicity when that
// version still offers it, otherwise its first one — the server's own fallback when a draft takes the new version.
export function newerPrice(sub) {
  const periods = sub?.newer_version?.periods || [];
  if (!periods.length) return null;
  const same = periods.find((p) => p.periodicity === sub.periodicity);
  return same || periods[0];
}

// Days of a trial from its start to its last free day, both included (the form pre-fills start + offer days − 1).
export function trialLength(startIso, endIso) {
  if (!startIso || !endIso) return null;
  const toDay = (iso) => { const [y, m, d] = iso.split("-").map(Number); return Date.UTC(y, m - 1, d) / 86400000; };
  const days = toDay(endIso) - toDay(startIso) + 1;
  return days > 0 ? days : null;
}

export function stateTone(state) {
  return {
    overdue: "bg-red-50 text-red-700 border-red-200", paid: "bg-emerald-50 text-emerald-700 border-emerald-200",
    partially_paid: "bg-amber-50 text-amber-800 border-amber-200", invoiced: "bg-sky-50 text-sky-700 border-sky-200",
    to_invoice: "bg-slate-50 text-slate-700 border-slate-200", cancelled: "bg-slate-50 text-slate-400 border-slate-200",
  }[state] || "bg-slate-50 text-slate-700 border-slate-200";
}

export function statusTone(status) {
  return {
    active: "bg-emerald-50 text-emerald-700 border-emerald-200", trial: "bg-sky-50 text-sky-700 border-sky-200",
    paused: "bg-amber-50 text-amber-800 border-amber-200", draft: "bg-slate-50 text-slate-700 border-slate-200",
    ended: "bg-slate-50 text-slate-500 border-slate-200", cancelled: "bg-slate-50 text-slate-400 border-slate-200",
  }[status] || "bg-slate-50 text-slate-700 border-slate-200";
}

// The sum of installments' remaining amounts, per currency (never added across currencies).
export function totalsByCurrency(items, field = "remaining_minor") {
  const out = {};
  for (const item of items || []) out[item.currency] = (out[item.currency] || 0) + (item[field] || 0);
  return out;
}

export function billingError(error, fallback) {
  const detail = error?.response?.data?.detail;
  if (typeof detail === "string") return detail;
  if (detail?.msg) return detail.msg;
  if (Array.isArray(detail)) return "Vérifiez les champs saisis.";
  return fallback;
}

export const newKey = () => `b-${Math.random().toString(16).slice(2)}${Date.now().toString(16)}`;
